import { z } from 'zod';
import { geocoder } from '@/shared/geo/geocodage';
import { supabase } from '@/shared/supabase/client';
import type { DepotEntreprises, Position, Site } from '../types';
import { cheminDocumentEntreprise } from './chemins';
import {
  COLONNES_ENTREPRISE,
  COLONNES_SITE,
  SELECTION_FICHE_ENTREPRISE,
  SELECTION_RESUME_ENTREPRISE,
  coordonneesValides,
  doitRegeocoder,
  ligneDePosition,
  ligneDeSaisieContact,
  ligneDeSaisieEntreprise,
  ligneDeSaisieSite,
  ligneEntreprise,
  ligneFicheEntreprise,
  ligneResumeEntreprise,
  ligneSite,
  messageErreur,
  versEntreprise,
  versFicheEntreprise,
  versResumeEntreprise,
  versSite,
} from './lignes';
import { messageRefus } from './lignes-chantiers';
import { adresseSignee, deposerPuisEnregistrer, retirerFichiers } from './stockage';

const lignesId = z.array(z.object({ id: z.string() }));
const lignesChemin = z.array(z.object({ chemin: z.string() }));

const AVEC_CHANTIERS_SITE = { '23503': 'Ce site a des chantiers : supprimez-les d’abord.' };

/* Une modification que l'accès par ligne refuse ne renvoie pas d'erreur : elle
   ne touche simplement aucune ligne. On demande l'id en retour pour le savoir. */
function verifierTouche(data: unknown, introuvable: string): void {
  if (lignesId.parse(data ?? []).length === 0) throw new Error(introuvable);
}

/* Un géocodage raté ne bloque jamais l'enregistrement : le site part sans position. */
async function geocoderSansEchec(ligne: {
  adresse: string;
  code_postal: string;
  commune: string;
}): Promise<Position | null> {
  try {
    return await geocoder({
      adresse: ligne.adresse,
      codePostal: ligne.code_postal,
      commune: ligne.commune,
    });
  } catch {
    return null;
  }
}

async function lireSite(id: string, echec: string): Promise<Site> {
  const { data, error } = await supabase()
    .from('sites')
    .select(COLONNES_SITE)
    .eq('id', id)
    .maybeSingle();
  if (error) throw new Error(messageErreur(error, echec));
  if (data === null) throw new Error('Ce site n’existe plus.');
  return versSite(ligneSite.parse(data));
}

export const entreprisesSupabase: DepotEntreprises = {
  async lister() {
    const { data, error } = await supabase()
      .from('entreprises')
      .select(SELECTION_RESUME_ENTREPRISE)
      .order('raison_sociale');
    if (error) throw new Error(messageErreur(error, 'Les entreprises n’ont pas pu être lues.'));
    return z.array(ligneResumeEntreprise).parse(data).map(versResumeEntreprise);
  },

  async lire(id) {
    const { data, error } = await supabase()
      .from('entreprises')
      .select(SELECTION_FICHE_ENTREPRISE)
      .eq('id', id)
      .maybeSingle();
    if (error) throw new Error(messageErreur(error, 'La fiche entreprise n’a pas pu être lue.'));
    if (data === null) return null;
    return versFicheEntreprise(ligneFicheEntreprise.parse(data));
  },

  async creer(saisie) {
    const { data, error } = await supabase()
      .from('entreprises')
      .insert(ligneDeSaisieEntreprise(saisie))
      .select(COLONNES_ENTREPRISE)
      .single();
    if (error) throw new Error(messageRefus(error, 'L’entreprise n’a pas pu être créée.'));
    return versEntreprise(ligneEntreprise.parse(data));
  },

  async modifier(id, saisie) {
    const { data, error } = await supabase()
      .from('entreprises')
      .update(ligneDeSaisieEntreprise(saisie))
      .eq('id', id)
      .select('id');
    if (error) throw new Error(messageRefus(error, 'L’entreprise n’a pas pu être modifiée.'));
    verifierTouche(data, 'Cette entreprise n’existe plus.');
  },

  async declarerNonRealisable(etapeId, note) {
    const { error } = await supabase().rpc('declarer_non_realisable', {
      p_etape: etapeId,
      p_note: note,
    });
    if (error)
      throw new Error(messageRefus(error, 'Le chantier n’a pas pu être déclaré non réalisable.'));
  },

  async marquerNonRealisable(id, note) {
    const { error } = await supabase().rpc('marquer_non_realisable', {
      p_entreprise: id,
      p_note: note,
    });
    if (error) throw new Error(messageRefus(error, 'L’entreprise n’a pas pu être marquée.'));
  },

  async retablirRealisable(id) {
    const { error } = await supabase().rpc('retablir_realisable', { p_entreprise: id });
    if (error) throw new Error(messageRefus(error, 'La mention n’a pas pu être retirée.'));
  },

  async supprimer(id) {
    /* Le client et tout son dossier (db/024) ; la base rend les fichiers à
       effacer du stockage, qu'elle ne peut pas effacer elle-même. */
    const reponse = await supabase().rpc('supprimer_entreprise_complete', {
      p_entreprise: id,
    });
    if (reponse.error)
      throw new Error(messageErreur(reponse.error, 'L’entreprise n’a pas pu être supprimée.'));
    const chemins: unknown = reponse.data;
    await retirerFichiers(z.array(z.string()).parse(chemins ?? []));
  },

  async ajouterSite(entrepriseId, saisie) {
    const ligne = ligneDeSaisieSite(saisie);
    const position = await geocoderSansEchec(ligne);
    const { error } = await supabase()
      .from('sites')
      .insert({ entreprise_id: entrepriseId, ...ligne, ...ligneDePosition(position) });
    if (error) throw new Error(messageErreur(error, 'Le site n’a pas pu être ajouté.'));
  },

  async modifierSite(id, saisie) {
    const actuel = await lireSite(id, 'Le site n’a pas pu être modifié.');
    const ligne = ligneDeSaisieSite(saisie);
    /* Adresse inchangée : la position reste telle quelle, manuelle comprise. */
    const position = doitRegeocoder(actuel, saisie)
      ? ligneDePosition(await geocoderSansEchec(ligne))
      : {};
    const { data, error } = await supabase()
      .from('sites')
      .update({ ...ligne, ...position })
      .eq('id', id)
      .select('id');
    if (error) throw new Error(messageErreur(error, 'Le site n’a pas pu être modifié.'));
    verifierTouche(data, 'Ce site n’existe plus.');
  },

  async positionnerSite(id, coordonnees) {
    let position: Position | null;
    if (coordonnees === null) {
      const actuel = await lireSite(id, 'La position du site n’a pas pu être recalculée.');
      position = await geocoderSansEchec({
        adresse: actuel.adresse,
        code_postal: actuel.codePostal,
        commune: actuel.commune,
      });
    } else {
      if (!coordonneesValides(coordonnees)) {
        throw new Error(
          'Position impossible : la latitude va de -90 à 90, la longitude de -180 à 180.'
        );
      }
      position = {
        latitude: coordonnees.latitude,
        longitude: coordonnees.longitude,
        precision: 'manuelle',
      };
    }
    const { data, error } = await supabase()
      .from('sites')
      .update(ligneDePosition(position))
      .eq('id', id)
      .select('id');
    if (error)
      throw new Error(messageErreur(error, 'La position du site n’a pas pu être enregistrée.'));
    verifierTouche(data, 'Ce site n’existe plus.');
    return position;
  },

  async supprimerSite(id) {
    const { error } = await supabase().from('sites').delete().eq('id', id);
    if (error) {
      throw new Error(
        messageErreur(error, 'Le site n’a pas pu être supprimé.', AVEC_CHANTIERS_SITE)
      );
    }
  },

  async ajouterContact(entrepriseId, saisie) {
    const { error } = await supabase()
      .from('contacts')
      .insert({ entreprise_id: entrepriseId, ...ligneDeSaisieContact(saisie) });
    if (error) throw new Error(messageErreur(error, 'Le contact n’a pas pu être ajouté.'));
  },

  async modifierContact(id, saisie) {
    const { data, error } = await supabase()
      .from('contacts')
      .update(ligneDeSaisieContact(saisie))
      .eq('id', id)
      .select('id');
    if (error) throw new Error(messageErreur(error, 'Le contact n’a pas pu être modifié.'));
    verifierTouche(data, 'Ce contact n’existe plus.');
  },

  async supprimerContact(id) {
    const { error } = await supabase().from('contacts').delete().eq('id', id);
    if (error) throw new Error(messageErreur(error, 'Le contact n’a pas pu être supprimé.'));
  },

  async ajouterDocument(entrepriseId, fichier, categorie) {
    const chemin = cheminDocumentEntreprise(entrepriseId, fichier.name, crypto.randomUUID());
    await deposerPuisEnregistrer(chemin, fichier, () =>
      supabase().from('documents_entreprise').insert({
        entreprise_id: entrepriseId,
        categorie,
        nom: fichier.name,
        taille: fichier.size,
        type_mime: fichier.type,
        chemin,
      })
    );
  },

  async supprimerDocument(id) {
    const { data, error } = await supabase()
      .from('documents_entreprise')
      .delete()
      .eq('id', id)
      .select('chemin');
    if (error) throw new Error(messageErreur(error, 'Le document n’a pas pu être supprimé.'));
    await retirerFichiers(lignesChemin.parse(data).map((d) => d.chemin));
  },

  adresseDocument(chemin) {
    return adresseSignee(chemin);
  },
};
